/**
 * SliderControl Component - Range slider for adjusting format planned cases
 * Requirements: 3.8
 */

import { useCallback, useMemo } from 'react';

interface SliderControlProps {
  format: string;
  currentValue: number;
  maxValue: number;
  baselineValue: number;
  onChange: (value: number) => void;
  warning?: string;
}

function SliderControl({
  format,
  currentValue,
  maxValue,
  baselineValue,
  onChange,
  warning,
}: SliderControlProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(parseInt(e.target.value, 10));
    },
    [onChange]
  );

  const delta = currentValue - baselineValue;
  const deltaPercent = baselineValue > 0 ? (delta / baselineValue) * 100 : 0;
  const baselinePosition = maxValue > 0 ? (baselineValue / maxValue) * 100 : 0;

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toLocaleString();
  };

  const deltaColor = useMemo(() => {
    if (delta > 0) return '#28a745';
    if (delta < 0) return '#dc3545';
    return '#666';
  }, [delta]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.format}>{format}</span>
        <div style={styles.values}>
          <span style={styles.currentValue}>{formatNumber(currentValue)}</span>
          {delta !== 0 && (
            <span style={{ ...styles.delta, color: deltaColor }}>
              ({delta > 0 ? '+' : ''}{formatNumber(delta)} / {deltaPercent > 0 ? '+' : ''}{deltaPercent.toFixed(1)}%)
            </span>
          )}
        </div>
      </div>

      <div style={styles.sliderContainer}>
        {/* Baseline marker */}
        <div
          style={{
            ...styles.baselineMarker,
            left: `${baselinePosition}%`,
          }}
          title={`Baseline: ${formatNumber(baselineValue)}`}
        />
        
        <input
          type="range"
          min={0}
          max={maxValue}
          value={currentValue}
          onChange={handleChange}
          style={styles.slider}
        />
      </div>

      <div style={styles.rangeLabels}>
        <span>0</span>
        <span style={styles.baselineLabel}>
          Baseline: {formatNumber(baselineValue)}
        </span>
        <span>{formatNumber(maxValue)}</span>
      </div>

      {warning && (
        <div style={styles.warning}>
          ⚠️ {warning}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  format: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
  },
  values: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  currentValue: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#333',
  },
  delta: {
    fontSize: '0.75rem',
  },
  sliderContainer: {
    position: 'relative',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
  },
  baselineMarker: {
    position: 'absolute',
    width: '2px',
    height: '16px',
    backgroundColor: '#0066cc',
    top: '4px',
    transform: 'translateX(-50%)',
    zIndex: 1,
    pointerEvents: 'none',
  },
  slider: {
    width: '100%',
    height: '6px',
    cursor: 'pointer',
    accentColor: '#0066cc',
  },
  rangeLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.7rem',
    color: '#888',
    marginTop: '0.25rem',
  },
  baselineLabel: {
    color: '#0066cc',
    fontWeight: 500,
  },
  warning: {
    marginTop: '0.5rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#fff3cd',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: '#856404',
  },
};

export default SliderControl;
